import { startTransition, useState } from 'react';

import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Popover from '@mui/material/Popover';
import Stack from '@mui/material/Stack';
import { alpha } from '@mui/material/styles';
import Typography from '@mui/material/Typography';

import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Iconify from '../../../components/iconify';
import { logoutUser } from '../../../features/users';

// ----------------------------------------------------------------------

const MENU_OPTIONS = [
  {
    label: 'Настройки на профил',
    icon: 'eva:person-outline',
    color: '#2196F3',
    description: 'Управление на лични данни'
  },
  {
    label: 'Информация за фирмата и контрагенти',
    icon: 'eva:home-outline',
    color: '#4CAF50',
    description: 'Фирмени настройки'
  },
  {
    label: 'Осигурителни данни',
    icon: 'eva:shield-outline',
    color: '#FF6722',
    description: 'Управление на осигурителни данни'
  },
  {
    label: 'Болнични',
    icon: 'eva:plus-outline',
    color: '#FF69B4',
    description: 'Управление на болнични'
  },
  {
    label: "Банкови сметки",
    icon: 'eva:credit-card-outline',
    color: '#FF9800',
    description: 'Управление на сметки'
  },
];

// ----------------------------------------------------------------------

export default function AccountPopover() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [open, setOpen] = useState(null);

  const { fname, lname, email } = useSelector((state) => state.users.user);

  const displayName = fname + ' ' + lname;

  const handleOpen = (event) => {
    setOpen(event.currentTarget);
  };

  const handleClose = () => {
    setOpen(null);
  };

  const handleLogout = () => {
    startTransition(() => {
      dispatch(logoutUser());
      navigate('/login', { replace: true });    
    });
  };


  return (
    <>
      <IconButton
        onClick={handleOpen}
        sx={{
          width: 40,
          height: 40,
          background: (theme) => alpha(theme.palette.grey[500], 0.08),
          transition: 'all 0.3s ease',
          '&:hover': {
            background: (theme) => alpha(theme.palette.primary.main, 0.1),
            transform: 'scale(1.05)',
          },
          ...(open && {
            background: (theme) =>
              `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`,
            transform: 'scale(1.05)',
          }),
        }}
      >
        <Avatar
          alt={displayName}
          sx={{
            width: 36,
            height: 36,
            border: (theme) => `solid 2px ${theme.palette.background.default}`,
            background: (theme) => `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`,
            fontWeight: 'bold',
            fontSize: '1.1rem',
          }}
        >
          {displayName.charAt(0).toUpperCase()}
        </Avatar>
       
      </IconButton>

      <Popover
        open={!!open}
        anchorEl={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            p: 0,
            mt: 1,
            ml: 0.75,
            width: 320,
            borderRadius: 2,
            boxShadow: (theme) => `0 8px 32px ${alpha(theme.palette.common.black, 0.12)}`,
            border: (theme) => `1px solid ${alpha(theme.palette.divider, 0.08)}`,
          },
        }}
      >
        {/* Header Section */}
        <Box 
          sx={{ 
            p: 3, 
            background: (theme) => `linear-gradient(135deg, ${alpha(theme.palette.primary.light, 0.1)} 0%, ${alpha(theme.palette.primary.main, 0.05)} 100%)`,
            borderBottom: (theme) => `1px solid ${alpha(theme.palette.divider, 0.08)}`,
          }}
        >
          <Stack direction="row" spacing={2} alignItems="center">
            <Avatar
              alt={displayName}
              sx={{
                width: 48,
                height: 48,
                background: (theme) => `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`,
                fontWeight: 'bold',
                fontSize: '1.2rem',
                boxShadow: (theme) => `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
              }}
            >
              {displayName.charAt(0).toUpperCase()}
            </Avatar>
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                {displayName}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                {email}
              </Typography>
              <Chip 
                label="Активен" 
                size="small" 
                color="success" 
                variant="soft"
                sx={{ 
                  fontSize: '0.75rem',
                  height: 20,
                  '& .MuiChip-label': { px: 1 }
                }}
              />
            </Box>
          </Stack>
        </Box>

        {/* Menu Items */}
        <Box sx={{ py: 1 }}>
          {MENU_OPTIONS.map((option, index) => (
            <MenuItem 
              key={option.label} 
              onClick={() => {
                if (option.label === 'Настройки на профил') {
                  navigate('/profile-settings');
                } else if (option.label === 'Информация за фирмата и контрагенти') {
                  navigate('/company-settings');
                } else if (option.label === 'Моят абонаментен план') {
                  navigate('/subscription');
                } else if (option.label === "Банкови сметки") { 
                  navigate('/bank-accounts')
                } else if (option.label === "Осигурителни данни") {
                  navigate('/insurance-data')
                } else if (option.label === "Болнични") {
                  navigate('/sick-leaves')
                }
                handleClose();
              }}
              sx={{
                mx: 1,
                my: 0.5,
                borderRadius: 1,
                py: 1.5,
                px: 2,
                transition: 'all 0.2s ease',
                '&:hover': {
                  background: (theme) => alpha(theme.palette.primary.main, 0.08),
                  transform: 'translateX(4px)',
                },
              }}
            >
              <Stack direction="row" spacing={2} alignItems="flex-start" sx={{ width: '100%' }}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: (theme) => alpha(option.color, 0.08),
                    color: option.color,
                    border: (theme) => `1px solid ${alpha(option.color, 0.2)}`,
                    flexShrink: 0,
                    mt: 0.5,
                  }}
                >
                  <Iconify icon={option.icon} width={22} />
                </Box>
                <Box sx={{ flexGrow: 1, minWidth: 0, pt: 0.5 }}>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      fontWeight: 500, 
                      mb: 0.5,
                      lineHeight: 1.2,
                      wordBreak: 'break-word'
                    }}
                  >
                    {option.label}
                  </Typography>
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      color: 'text.secondary',
                      lineHeight: 1.2,
                      display: 'block'
                    }}
                  >
                    {option.description}
                  </Typography>
                </Box>
              </Stack>
            </MenuItem>
          ))}
        </Box>

        <Divider sx={{ borderStyle: 'dashed', mx: 2 }} />

        {/* Logout Section */}
        <Box sx={{ p: 1 }}>
          <MenuItem
            disableRipple
            disableTouchRipple
            onClick={handleLogout}
            sx={{
              mx: 1,
              borderRadius: 1,
              py: 1.5,
              px: 2,
              transition: 'all 0.2s ease',
              '&:hover': {
                background: (theme) => alpha(theme.palette.error.main, 0.08),
                transform: 'translateX(4px)',
              },
            }}
          >
            <Stack direction="row" spacing={2} alignItems="center">
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 1.5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: (theme) => alpha(theme.palette.error.main, 0.08),
                  color: 'error.main',
                  border: (theme) => `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
                  flexShrink: 0,
                }}
              >
                <Iconify icon="eva:log-out-outline" width={22} />
              </Box>
              <Typography variant="body2" sx={{ fontWeight: 500, color: 'error.main' }}>
                Изход
              </Typography>
            </Stack>
          </MenuItem>
        </Box>
      </Popover>
    </>
  );
}
